import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './GuidePages.css';
import GuideAdSlot from './GuideAdSlot';
import { GUIDE_WOLOF_CONTENT, WolofParagraph } from '../data/guideWolofContent';

const renderParts = (parts: WolofParagraph) =>
  parts.map((part, i) => {
    if (part.tag === 'em') return <em key={i}>{part.text}</em>;
    if (part.tag === 'strong') return <strong key={i}>{part.text}</strong>;
    return <React.Fragment key={i}>{part.text}</React.Fragment>;
  });

const GuideWolof: React.FC = () => {
  const { i18n } = useTranslation();
  const c = GUIDE_WOLOF_CONTENT[i18n.language === 'en' ? 'en' : 'fr'];

  useEffect(() => {
    document.title = c.pageTitle;
  }, [c.pageTitle]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 jg-guide-page">

      <nav style={{ marginBottom: '1.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
        <Link to="/" style={{ color: 'var(--color-accent)', textDecoration: 'none' }}>{c.breadcrumbHome}</Link>
        {' › '}
        <Link to="/about" style={{ color: 'var(--color-accent)', textDecoration: 'none' }}>{c.breadcrumbGuides}</Link>
        {' › '}
        <span>{c.breadcrumbCurrent}</span>
      </nav>

      <h1 className="text-3xl font-bold mb-4">
        {c.h1}
      </h1>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem', fontSize: '1.05rem' }}>
        {c.intro}
      </p>

      <div className="prose prose-lg max-w-none">

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            {c.whatIsTitle}
          </h2>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {renderParts(c.whatIsP1)}
          </p>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {renderParts(c.whatIsP2)}
          </p>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {renderParts(c.whatIsP3)}
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            {c.phonologyTitle}
          </h2>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {renderParts(c.phonologyP1)}
          </p>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {renderParts(c.phonologyP2)}
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            {c.grammarTitle}
          </h2>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {renderParts(c.grammarP1)}
          </p>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {renderParts(c.grammarP2)}
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            {c.greetingsTitle}
          </h2>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {renderParts(c.greetingsP1)}
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1.5rem' }}>
              <thead>
                <tr style={{ background: 'var(--color-accent)', color: '#fff' }}>
                  <th style={{ padding: '10px 14px', textAlign: 'left' }}>{c.greetingsTableHeaders[0]}</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left' }}>{c.greetingsTableHeaders[1]}</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left' }}>{c.greetingsTableHeaders[2]}</th>
                </tr>
              </thead>
              <tbody>
                {c.greetingsRows.map(([fr, wf, pron]) => (
                  <tr key={fr} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 14px', color: 'var(--text)' }}>{fr}</td>
                    <td style={{ padding: '8px 14px', fontWeight: 600, color: 'var(--color-accent)' }}>{wf}</td>
                    <td style={{ padding: '8px 14px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>{pron}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            {c.familyTitle}
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1.5rem' }}>
              <thead>
                <tr style={{ background: 'var(--color-accent)', color: '#fff' }}>
                  <th style={{ padding: '10px 14px', textAlign: 'left' }}>{c.familyTableHeaders[0]}</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left' }}>{c.familyTableHeaders[1]}</th>
                </tr>
              </thead>
              <tbody>
                {c.familyRows.map(([fr, wf]) => (
                  <tr key={fr} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 14px', color: 'var(--text)' }}>{fr}</td>
                    <td style={{ padding: '8px 14px', fontWeight: 600, color: 'var(--color-accent)' }}>{wf}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            {c.numbersTitle}
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1.5rem' }}>
              <thead>
                <tr style={{ background: 'var(--color-accent)', color: '#fff' }}>
                  <th style={{ padding: '10px 14px', textAlign: 'left' }}>{c.numbersTableHeaders[0]}</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left' }}>{c.numbersTableHeaders[1]}</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left' }}>{c.numbersTableHeaders[2]}</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left' }}>{c.numbersTableHeaders[3]}</th>
                </tr>
              </thead>
              <tbody>
                {c.numbersRows.map(([a, b]) => (
                  <tr key={a} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 14px', color: 'var(--text)' }}>{a.split(' — ')[0]}</td>
                    <td style={{ padding: '8px 14px', fontWeight: 600, color: 'var(--color-accent)' }}>{a.split(' — ')[1]}</td>
                    <td style={{ padding: '8px 14px', color: 'var(--text)' }}>{b.split(' — ')[0]}</td>
                    <td style={{ padding: '8px 14px', fontWeight: 600, color: 'var(--color-accent)' }}>{b.split(' — ')[1]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {renderParts(c.numbersP1)}
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            {c.cultureTitle}
          </h2>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {renderParts(c.cultureP1)}
          </p>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {renderParts(c.cultureP2)}
          </p>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {c.cultureP3}
          </p>
        </section>

        <section className="mb-8 jg-guide-callout" style={{ background: 'var(--surface-raised)', borderRadius: '12px', padding: '1.5rem' }}>
          <h2 className="text-2xl font-semibold mb-4">
            {c.practiceTitle}
          </h2>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {c.practiceP1}
          </p>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <Link
              to="/"
              style={{
                background: 'var(--color-accent)', color: '#fff', padding: '0.75rem 1.5rem',
                borderRadius: '10px', textDecoration: 'none', fontWeight: 700, display: 'inline-block'
              }}
            >
              {c.ctaStart}
            </Link>
            <Link
              to="/guide/bambara"
              style={{
                background: 'transparent', color: 'var(--color-accent)', padding: '0.75rem 1.5rem',
                borderRadius: '10px', textDecoration: 'none', fontWeight: 600,
                border: '1.5px solid var(--color-accent)', display: 'inline-block'
              }}
            >
              {c.ctaBambara}
            </Link>
            <Link
              to="/guide/swahili"
              style={{
                background: 'transparent', color: 'var(--color-accent)', padding: '0.75rem 1.5rem',
                borderRadius: '10px', textDecoration: 'none', fontWeight: 600,
                border: '1.5px solid var(--color-accent)', display: 'inline-block'
              }}
            >
              {c.ctaSwahili}
            </Link>
          </div>
        </section>

      </div>

      <GuideAdSlot />

      <footer style={{ marginTop: '3rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border)', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
        <Link to="/" style={{ color: 'var(--color-accent)', marginRight: '1rem' }}>{c.footerHome}</Link>
        <Link to="/about" style={{ color: 'var(--color-accent)', marginRight: '1rem' }}>{c.footerAbout}</Link>
        <Link to="/guide/bambara" style={{ color: 'var(--color-accent)', marginRight: '1rem' }}>{c.footerBambara}</Link>
        <Link to="/guide/swahili" style={{ color: 'var(--color-accent)' }}>{c.footerSwahili}</Link>
      </footer>
    </div>
  );
};

export default GuideWolof;
